import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Stack,
    Typography,
} from '@mui/material';
import {
    useNotify,
    Form,
    ReferenceInput,
    AutocompleteInput,
    SaveHandler,
} from 'react-admin';
import {
    memberInputText,
    memberOptionText,
} from '@app/member/MemberInputOption';
import {
    MemberId,
    MissionSummary,
    MissionSessionId,
    addSessionParticipant,
} from '@wwf/sdk';
import {
    collectDistinctParticipantIdsFromMissionSessions,
    missionSummaryGetOneQueryKey,
} from '@domain/mission';

interface MissionParticipantAddFormProps {
    record: MissionSummary;
    sessionId: MissionSessionId;
    participantIds?: MemberId[];
}

interface MissionParticipantAddFormData {
    new_participant: MemberId;
}

export const MissionParticipantAddForm = ({
    record,
    sessionId,
    participantIds,
}: MissionParticipantAddFormProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [onlyCurrentAntenna, setOnlyCurrentAntenna] = useState(true);

    const notify = useNotify();
    const queryClient = useQueryClient();
    const existingParticipantIds =
        participantIds ??
        collectDistinctParticipantIdsFromMissionSessions(record.sessions);

    const handleAddParticipant: SaveHandler<
        MissionParticipantAddFormData
    > = async (data: Partial<MissionParticipantAddFormData>) => {
        const selectedMemberId = data.new_participant;

        if (!selectedMemberId) {
            notify('Please select a member to add', { type: 'warning' });
            return;
        }

        if (existingParticipantIds?.includes(selectedMemberId)) {
            notify('This member is already a participant', { type: 'warning' });
            return;
        }

        setIsSubmitting(true);
        try {
            await addSessionParticipant(record.id, sessionId, selectedMemberId);
            notify('Participant added successfully', { type: 'success' });
            await queryClient.invalidateQueries({
                queryKey: missionSummaryGetOneQueryKey(record.id),
            });
        } catch (error) {
            console.error('Error adding participant:', error);
            notify(
                `Failed to add participant: ${error instanceof Error ? error.message : 'Unknown error'}`,
                { type: 'error' }
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            p={2}
            border="1px solid"
            borderColor="divider"
            borderRadius={1}
            mb={2}
        >
            <Typography variant="h6" gutterBottom>
                Add Participant
            </Typography>
            <Form<MissionParticipantAddFormData>
                onSubmit={handleAddParticipant}
            >
                <Stack gap={0.5}>
                    <Stack direction="row" alignContent="center" gap={2}>
                        <ReferenceInput
                            source="new_participant"
                            reference="member"
                            enableGetChoices={() =>
                                !onlyCurrentAntenna || !!record.antenna?.id
                            }
                            filter={{
                                ...(onlyCurrentAntenna &&
                                    record.antenna?.id && {
                                        antennaId: record.antenna.id,
                                    }),
                                // Exclude already selected participants
                                ...(existingParticipantIds?.length && {
                                    'id@not.in': existingParticipantIds,
                                }),
                            }}
                            sort={{
                                field: 'firstName',
                                order: 'ASC',
                            }}
                        >
                            <AutocompleteInput
                                disabled={
                                    onlyCurrentAntenna && !record.antenna?.id
                                }
                                label="Select member"
                                optionText={memberOptionText}
                                inputText={memberInputText}
                                filterToQuery={search => ({ search })}
                                helperText={
                                    onlyCurrentAntenna && !record.antenna?.id
                                        ? 'No antenna selected'
                                        : false
                                }
                            />
                        </ReferenceInput>
                        <Button
                            type="submit"
                            disabled={
                                isSubmitting ||
                                (onlyCurrentAntenna && !record.antenna?.id)
                            }
                            sx={{ flexShrink: 0 }}
                        >
                            {isSubmitting ? 'Adding...' : 'Add Participant'}
                        </Button>
                    </Stack>

                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={onlyCurrentAntenna}
                                onChange={(_, c) => setOnlyCurrentAntenna(c)}
                            />
                        }
                        label={
                            <Typography
                                variant="caption"
                                component="span"
                                color="text.secondary"
                                lineHeight={1.25}
                            >
                                Only members from the current mission antenna
                            </Typography>
                        }
                        sx={{
                            m: 0,
                            alignItems: 'center',
                            gap: 0.5,
                            ml: 0.25,
                        }}
                    />
                </Stack>
            </Form>
        </Box>
    );
};
